import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileBarChart, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FeatureSwitch from '../../components/FeatureSwitch'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import { useToast } from '../../components/toastContext'
import { dateColumn, flagColumn, indexColumn, statusColumn } from './cells'
import AssessmentFormDrawer from './AssessmentFormDrawer'
import {
  deleteAssessment,
  getAssessmentsPage,
  setAssessmentsEnabled,
  listAssessments,
  saveAssessment,
} from './engagementService'
import { assessmentsStrings as S } from './strings'
import './engagement.css'
import { serverText } from '../../lib/i18n'

/** إدارة القياسات — Views/AssessmentsManagement/Index.cshtml. */
export default function AssessmentsPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [featureOn, setFeatureOn] = useState(true)
  /* REMEMBERED, not plain state: the table's page came back on a Back and the search
     that chose those rows did not, so page 3 returned over an unsearched list. */
  const [query, setQuery] = useRemembered('assessments:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [options, setOptions] = useState({})

  const openForm = (assessment) => {
    setEditing(assessment ?? null)
    setFormOpen(true)
  }

  const [reloadKey, setReloadKey] = useState(0)

  /* The topics and groups come from this screen's OWN payload, not the shared engagement
     options — it returns them filtered to what an assessment may draw from. */
  useEffect(() => {
    getAssessmentsPage()
      .then((page) => {
        setOptions({ categories: page.categories, groups: page.groups })
        setFeatureOn(page.enabled)
      })
      .catch(() => setOptions({ categories: [], groups: [] }))
  }, [])

  /* Optimistic, and put back if the server refuses. */
  /* Shared with every other activation switch — see useFeatureToggle. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: setFeatureOn,
    save: setAssessmentsEnabled,
  })

  useEffect(() => {
    setLoading(true)
    listAssessments()
      .then((response) => {
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
  }, [reloadKey, toast])

  const columns = useMemo(
    () => [
      indexColumn(S.columns.index),
      {
        key: 'title',
        header: S.columns.title,
        render: (row) => (
          <bdi className="eng__title" title={row.title}>
            {row.title}
          </bdi>
        ),
      },
      dateColumn('displayStartDate', S.columns.startDate),
      dateColumn('displayEndDate', S.columns.endDate),
      statusColumn('isActive', S.columns.status, S),
      flagColumn('mandatory', S.columns.mandatory, S),
      { key: 'passed', header: S.columns.passed, align: 'center' },
      { key: 'failed', header: S.columns.failed, align: 'center' },
      { key: 'absent', header: S.columns.absent, align: 'center' },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              /* THE RESULTS. `التفاصيل` used to be missing from this row because the screen
                 behind it did not exist — the legacy borrows the course exam report for it,
                 and there was no port of that. There is now. */
              {
                id: 'report',
                icon: FileBarChart,
                label: S.row.details,
                tone: 'info',
                onClick: () => navigate(`/assessments-management/${row.id}/report`),
              },
              { id: 'update', icon: Pencil, label: S.row.update, onClick: () => openForm(row) },
              /* `إرسال بريد` IS NOT HERE. Nothing in this slice sends one — the
                 legacy's send action has no counterpart in the port, and a mail button
                 that opens nothing is worse than its absence. COSMETIC_UI.md. */
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={loading ? undefined : rows.length}
        actions={
          <>
            {/* LocChkBxEnable — present in the legacy view, unrendered until now. */}
            <FeatureSwitch checked={featureOn} onChange={toggleFeature} label={S.enableFeature} />
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.create}
            </Button>
          </>
        }
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.title }} />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'startDate', dir: 'desc' }}
      />

      <AssessmentFormDrawer
        open={formOpen}
        assessment={editing}
        categories={options.categories}
        groups={options.groups}
        onClose={() => setFormOpen(false)}
        /* This toasted "deleted" after a SAVE, and wrote nothing either way. */
        onSave={async (values) => {
          /* `GroupIds` is sent as a comma-separated string, and the server requires at
             least one — the drawer now collects them. `editing?.groupIds` remains the
             fallback so an edit that does not touch the picker keeps its audience. */
          const outcome = await saveAssessment({
            ...values,
            id: editing?.id,
            groupIds: values.groupIds ?? editing?.groupIds ?? [],
          })
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setFormOpen(false)
          toast.success(serverText(outcome.message) || S.saved)
          setReloadKey((key) => key + 1)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteAssessment(pendingDelete.id)
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
